import React, { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { tagsService } from "services/tags/tags.service"
import { TagSweepStatus } from "services/tags/type"

/** Poll while a run is in flight so the counters move; idle otherwise. */
const ACTIVE_POLL_MS = 5000
const IDLE_POLL_MS = 30000

/** A RUNNING row older than this is almost certainly a crashed run, not live work. */
const STALE_RUN_MS = 60 * 60 * 1000

export const SweepStatusBanner: React.FC = () => {
  const { t, i18n } = useTranslation("translation", { useSuspense: false })
  const [status, setStatus] = useState<TagSweepStatus | null>(null)

  const load = useCallback(async () => {
    const res = await tagsService.getSweepStatus()
    if (res) setStatus(res)
  }, [])

  // A primitive dependency on purpose: `status.activeRun` is a new object every poll, so
  // depending on it would restart the interval — and re-fetch — on every response.
  const isRunActive = Boolean(status?.activeRun)

  useEffect(() => {
    void load()
    const timer = setInterval(() => void load(), isRunActive ? ACTIVE_POLL_MS : IDLE_POLL_MS)
    return () => clearInterval(timer)
  }, [load, isRunActive])

  const formatCount = useCallback((value: number) => value.toLocaleString(i18n.language), [i18n.language])

  const formatWhen = useCallback(
    (value: string | null) => (value ? new Date(value).toLocaleString(i18n.language) : t("tags.sweep.never")),
    [i18n.language, t]
  )

  const formatElapsed = useCallback(
    (since: string) => {
      const minutes = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 60000))
      return minutes < 60
        ? t("tags.sweep.elapsedMinutes", { minutes })
        : t("tags.sweep.elapsedHours", { hours: Math.floor(minutes / 60), minutes: minutes % 60 })
    },
    [t]
  )

  if (!status) return null

  const { activeRun, cron, totalUsers } = status
  const stale = activeRun ? Date.now() - new Date(activeRun.startedAt).getTime() > STALE_RUN_MS : false

  return (
    <div className="mb-3 rounded-lg border border-base-300 bg-base-200 px-4 py-3 text-sm text-black dark:text-white">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        <span>
          <span className="opacity-70">{t("tags.sweep.progressLabel")}</span> <strong>{cron.progressPercent}%</strong>{" "}
          <span className="opacity-70">
            {t("tags.sweep.ofTotal", { swept: formatCount(cron.usersSwept), total: formatCount(totalUsers) })}
          </span>
        </span>

        <span>
          <span className="opacity-70">{t("tags.sweep.lastFullSweepLabel")}</span>{" "}
          <strong>{formatWhen(cron.lastCompletedAt)}</strong>
        </span>

        {activeRun && (
          <span>
            <span className="opacity-70">
              {t(activeRun.isAdminRun ? "tags.sweep.manualRunInProgress" : "tags.sweep.cronTickInProgress")}
            </span>{" "}
            <strong>{formatCount(activeRun.processed)}</strong>{" "}
            <span className="opacity-70">
              {t("tags.sweep.runDetail", {
                assigned: activeRun.assigned,
                removed: activeRun.removed,
                elapsed: formatElapsed(activeRun.startedAt)
              })}
            </span>
            {stale && <span className="ml-2 text-warning">— {t("tags.sweep.mayBeStalled")}</span>}
          </span>
        )}
      </div>

      {cron.lastCompletedAt === null && <div className="mt-1 opacity-70">{t("tags.sweep.noSweepYet")}</div>}
    </div>
  )
}
